// Angular
import { Injectable } from '@angular/core';

// Third-party
import { compare } from 'fast-json-patch';

// Local
import { isPathAllowed } from '@app/shared/patch/patch-validator';
import { isAppPatchOpAllowed } from '@app/services/utilities/patch-business-filter';
import { PatchModelType } from '@app/types/api/dot-net-api-types/patch-constraints';

/** Single JSON Patch operation aligned with RFC 6902 / API payloads. */
export interface JsonPatchOperation {
  op: 'add' | 'remove' | 'replace' | 'move' | 'copy' | 'test';
  path: string;
  value?: unknown;
  from?: string | null;
}

@Injectable({ providedIn: 'root' })
export class PatchHelpersService {
  /**
   * Generates RFC 6902 patch operations between two snapshots using fast-json-patch.
   * When modelType is provided, operations targeting [PatchDisabled] properties are
   * automatically filtered out via the constraint map in patch-constraints.ts.
   *
   * Prefer passing modelType -- callers without it skip readonly enforcement entirely.
   *
   * When current is a form raw value whose TypeScript shape diverges from T (e.g.
   * IRawAddressForm vs Address), adapt at the call site:
   *   const snapshot = formValue as unknown as T; // document the narrowing
   *   generatePatchOperations(original, snapshot, modelType)
   * See clerk-review.component.ts for the established precedent.
   *
   * isAppPatchOpAllowed (patch-business-filter.ts) is applied internally — callers do not
   * need to apply it. Remaining filter convention (apply after this call):
   *   1. Feature-specific filter (e.g. isClerkReviewBusinessOpAllowed)
   *   2. Model-specific validation (e.g. isJudgeOpValid) + transforms (e.g. stripJudgeName)
   */
  generatePatchOperations<T>(original: T, current: T, modelType?: PatchModelType): JsonPatchOperation[] {
    if (!original || !current) {
      return [];
    }

    const patches = compare(original as object, current as object);

    return patches
      .filter((patch) => this.isValidOperation(patch.op))
      .filter((patch) => !modelType || isPathAllowed(modelType, patch.path))
      .map((patch) => {
        const operation: JsonPatchOperation = {
          op: patch.op as JsonPatchOperation['op'],
          path: patch.path,
        };

        switch (patch.op) {
          case 'add':
          case 'replace':
          case 'test':
            operation.value = (patch as { value?: unknown }).value;
            break;

          case 'move':
          case 'copy':
            operation.from = (patch as { from: string }).from;
            if ('value' in patch) {
              operation.value = (patch as { value?: unknown }).value;
            }
            break;

          case 'remove':
            break;
        }

        return operation;
      })
      .filter(isAppPatchOpAllowed);
  }

  /**
   * Whether the op is one we send to the API (excludes e.g. internal `_get`).
   */
  private isValidOperation(op: string): op is JsonPatchOperation['op'] {
    return ['add', 'remove', 'replace', 'move', 'copy', 'test'].includes(op);
  }
}
